import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X, Command as CommandIcon } from "@phosphor-icons/react";
import api from "../lib/api";
import SafeSnippet from "./SafeSnippet";

const KIND_META = {
  invoice: { label: "Invoice", color: "text-[#FF3E00]" },
  journal: { label: "Journal", color: "text-emerald-700" },
  supplier: { label: "Supplier", color: "text-blue-700" },
  employee: { label: "Employee", color: "text-purple-700" },
  aml_case: { label: "AML case", color: "text-[#D0021B]" },
  bank_account: { label: "Bank account", color: "text-zinc-800" },
  contract: { label: "Contract (IFRS 15)", color: "text-indigo-700" },
  lease: { label: "Lease (IFRS 16)", color: "text-amber-700" },
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Ctrl/Cmd+K global shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else { setQ(""); setHits([]); setSelectedIdx(0); }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open || q.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q.trim())}&limit=8`);
        setHits(r.data.flat || []);
        setSelectedIdx(0);
      } catch (err) {
        setHits([]);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = useCallback((hit) => {
    setOpen(false);
    if (hit?.href) navigate(hit.href);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx((i) => Math.min(hits.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && hits[selectedIdx]) { e.preventDefault(); go(hits[selectedIdx]); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-btn"
        className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-500 border border-zinc-300 bg-zinc-50 hover:border-zinc-950 hover:text-zinc-950 transition-all"
        title="Search (Ctrl/Cmd+K)"
      >
        <MagnifyingGlass size={14} />
        <span>Search invoices, journals, cases…</span>
        <kbd className="ml-2 font-mono text-[10px] bg-white border border-zinc-300 px-1.5 py-0.5">⌘K</kbd>
      </button>
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-btn-mobile"
        className="md:hidden text-zinc-600 hover:text-zinc-950"
      >
        <MagnifyingGlass size={18} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] bg-zinc-950/40 backdrop-blur-sm flex items-start justify-center pt-24 px-4" onClick={() => setOpen(false)} data-testid="global-search-modal">
          <div className="w-full max-w-2xl bg-white border-2 border-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center border-b border-zinc-950 px-4 py-3 gap-3">
              <MagnifyingGlass size={18} className="text-zinc-500" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search invoices, journals, suppliers, employees, AML cases…"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="global-search-input"
              />
              {loading && <span className="text-[10px] font-mono text-zinc-400 pulse-dot">SEARCHING…</span>}
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-950">
                <X size={18} />
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto" data-testid="global-search-results">
              {q.trim().length < 2 && (
                <div className="px-4 py-8 text-center text-xs text-zinc-400 space-y-2">
                  <div>Type at least 2 characters to search.</div>
                  <div className="font-mono text-[10px]">
                    ↑↓ to navigate · <span className="border border-zinc-200 px-1">↵</span> to open · <span className="border border-zinc-200 px-1">esc</span> to close
                  </div>
                </div>
              )}
              {q.trim().length >= 2 && !loading && hits.length === 0 && (
                <div className="px-4 py-8 text-center text-xs text-zinc-400">
                  No matches for &ldquo;{q}&rdquo;
                </div>
              )}
              {hits.map((hit, i) => {
                const meta = KIND_META[hit.kind] || { label: hit.kind, color: "text-zinc-500" };
                const isSel = i === selectedIdx;
                return (
                  <button
                    key={`${hit.kind}-${hit.id}`}
                    onMouseEnter={() => setSelectedIdx(i)}
                    onClick={() => go(hit)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-zinc-100 transition-colors ${isSel ? "bg-zinc-950 text-white" : "hover:bg-zinc-50"}`}
                    data-testid={`global-search-result-${i}`}
                  >
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${isSel ? "border-white text-white" : `border-zinc-300 ${meta.color}`} tracking-wider uppercase min-w-[110px] text-center`}>
                      {meta.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{hit.title}</div>
                      <div className={`text-[11px] truncate ${isSel ? "text-zinc-400" : "text-zinc-500"}`}>{hit.subtitle}</div>
                      {hit.snippet && (
                        <SafeSnippet
                          snippet={hit.snippet}
                          className={`text-[11px] mt-0.5 truncate search-snippet ${isSel ? "text-zinc-300" : "text-zinc-500"}`}
                        />
                      )}
                    </div>
                    <span className={`font-mono text-[10px] ${isSel ? "text-zinc-400" : "text-zinc-300"}`}>{hit.href}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
